import { PanelLeft, PanelLeftClose, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import chatsService from "../../services/chatsService";
import {
  deleteChatStatus,
  fetchChats,
  setChatMessages,
  updateChatTitle,
} from "../../store/slices/dashboardSlice.js";
import * as S from "../dashboard/_shared.jsx";
import EvaChat from "../evaChat";
import EvaChatHistory from "../evaChatHistory";
import EvaInsights from "../evaInsights";
import {
  resolveSelectedPeriod,
  resolveSelectedCity,
  convertInsightsToRender,
  deriveApiChats,
  mergeAndSortChats,
} from "./dataTransforms";
import "./style.scss";

export default function EvaView({ ctx }) {
  const { C } = S;
  const { per, periodoG } = ctx;

  const dispatch = useDispatch();
  const chatsState = useSelector((state) => state.dashboard.chats);
  const chats = chatsState?.data?.data || [];
  const filtersState = useSelector((state) => state.filters);
  const selectedPeriod = resolveSelectedPeriod(filtersState);
  const selectedCity = resolveSelectedCity(filtersState);

  const insightsState = useSelector((state) => state.dashboard.insights);
  const insights = insightsState?.data?.data || [];

  const insightsToRender = convertInsightsToRender(insights);

  const [pendingChats, setPendingChats] = useState([]);

  // Ao montar a aba Eva, re-busca os chats para incluir os criados na sessão anterior
  useEffect(() => {
    dispatch(fetchChats());
  }, []);

  const handleCreateChat = async (object) => {
    try {
      const resposta = await chatsService.post(object);
      return resposta;
    } catch (error) {
      console.error("Erro ao criar chat:", error);
      return {
        role: "assistant",
        content: "Desculpe, ocorreu um erro ao criar o chat.",
      };
    }
  };

  const SAUDACAO_EVA = {
    role: "assistant",
    content:
      "Oi! Eu sou a Eva 👋, uma persona criada a partir de entrevistas reais para representar o seu cliente, traduzindo de forma humanizada suas experiências, expectativas e percepções. O que você quer saber sobre a minha experiência?",
  };
  const _agora = Date.now();
  const _h = 3600000;

  // derive chats from redux store into the shape expected by the UI
  const apiChats = deriveApiChats(chats);
  const evaChats = mergeAndSortChats(pendingChats, apiChats);
  const [evaChatAtivoId, setEvaChatAtivoId] = useState("c1");
  const [historicoEvaVisivel, setHistoricoEvaVisivel] = useState(true);

  // Quando os chats carregam e nenhum foi selecionado explicitamente,
  // sincroniza evaChatAtivoId com o primeiro chat disponível
  const firstChatId = evaChats[0]?.id;
  useEffect(() => {
    if (evaChatAtivoId === "c1" && firstChatId != null) {
      setEvaChatAtivoId(firstChatId);
    }
  }, [firstChatId]);

  const novoChatEva = async () => {
    const object = {
      fk_id_user: 1,
      title: "Nova conversa",
      messages: [SAUDACAO_EVA],
    };

    const response = await handleCreateChat(object);
    const created = response?.data?.data || response?.data;
    if (created?.id) {
      setPendingChats((prev) => [
        {
          id: created.id,
          title: created.title || object.title,
          createdAt: Date.now(),
          messages: object.messages.map((m) => ({
            role: m.role || (m.sender === "system" ? "assistant" : m.sender),
            content: m.content || m.message,
          })),
        },
        ...prev,
      ]);
      setEvaChatAtivoId(created.id);
    }
  };

  const selecionarChatEva = (id) => setEvaChatAtivoId(id);

  const excluirChatEva = async (id) => {
    await dispatch(deleteChatStatus(id));
    setPendingChats((prev) => prev.filter((c) => c.id !== id));
    const remaining = evaChats.filter((c) => c.id !== id);
    if (remaining.length > 0) {
      if (id === evaChatAtivoId) setEvaChatAtivoId(remaining[0].id);
    } else {
      await novoChatEva();
    }
  };

  const atualizarTituloChatEva = (chatId, newTitle) => {
    const isPending = pendingChats.some((c) => c.id === chatId);
    if (isPending) {
      setPendingChats((prev) =>
        prev.map((c) => (c.id === chatId ? { ...c, title: newTitle } : c)),
      );
    }
    dispatch(updateChatTitle({ chatId, title: newTitle }));
  };

  const excluirTodosChatsEva = async () => {
    if (!window.confirm("Excluir todas as conversas? Esta ação não pode ser desfeita.")) return;
    await chatsService.deleteAll();
    setPendingChats([]);
    dispatch(fetchChats());
  };

  const atualizarMensagensChatEva = (novasMsgs) => {
    // Atualiza pendingChats (chats criados nesta sessão)
    setPendingChats((prev) => {
      const idx = prev.findIndex((c) => c.id === evaChatAtivoId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], messages: novasMsgs };
      return next;
    });
    // Atualiza Redux também (chats carregados da API)
    dispatch(setChatMessages({ chatId: evaChatAtivoId, messages: novasMsgs }));
  };

  const chatAtivo =
    evaChats.find((c) => c.id === evaChatAtivoId) || evaChats[0];

  return (
    <div
      className="tabEvaView"
      style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14, alignItems: "stretch" }}
    >
      <EvaInsights insights={insightsToRender} />
      <section
        title="Conversa com a Eva"
        style={{
          background: C.surface,
          borderRadius: 12,
          border: `1px solid ${C.border}`,
          display: "flex",
          flexDirection: "column",
          height: 640,
          overflow: "hidden",
        }}
      >
        <div style={{ flex: 1, minHeight: 0, padding: "12px 16px", display: "flex", gap: 12, overflow: "hidden" }}>
          {/* Sidebar de histórico */}
          {historicoEvaVisivel && (
            <EvaChatHistory
              chats={evaChats}
              ativoId={evaChatAtivoId}
              onSelect={selecionarChatEva}
              onNew={novoChatEva}
              onDelete={excluirChatEva}
            />
          )}
          {/* Coluna direita: header + chat */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                background: C.g800,
                borderRadius: 12,
                marginBottom: 12,
                flexShrink: 0,
              }}
            >
              {/* Botão toggle do histórico */}
              <button
                onClick={() => setHistoricoEvaVisivel((v) => !v)}
                title={historicoEvaVisivel ? "Ocultar histórico" : "Mostrar histórico"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "rgba(255,255,255,.08)",
                  border: "1px solid rgba(255,255,255,.12)",
                  cursor: "pointer",
                  transition: "all .15s",
                  flexShrink: 0,
                  fontFamily: "inherit",
                  color: "#FFFFFF",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,.15)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; }}
              >
                {historicoEvaVisivel ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
              </button>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg,${C.g600},${C.p500})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Sparkles size={14} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Eva — Persona Sintética
                </div>
                <div style={{ color: "rgba(255,255,255,.4)", fontSize: 10 }}>
                  Dados de {selectedPeriod || per?.label}
                </div>
              </div>
            </div>
            <EvaChat
              key={`${chatAtivo ? chatAtivo.id : "empty"}`}
              chatAtivo={chatAtivo}
              periodName={selectedPeriod}
              cityFilter={selectedCity}
              messages={chatAtivo?.messages || []}
              onMessagesUpdate={atualizarMensagensChatEva}
              onTitleUpdate={atualizarTituloChatEva}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
